"use client";

import { useMemo, useRef, useState } from "react";
import { formOptions } from "@tanstack/react-form";
import { useTranslations } from "next-intl";

import type { HubSpotSubmitResult } from "@/app/_api/hubspot/shared/types";
import { Button } from "@/app/_ui/components/core/Button";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";

import { useAppForm } from "../shared/hooks/form";
import { SubmitButton } from "../shared/SubmitButton";
import { countryValidator, emailValidator, optionalString, requiredString, urlValidator } from "../shared/validators";
import type { ValidationMessages } from "../shared/types";
import { createValidationMessages, enrichWithUTMs } from "../shared/utils";

import {
  CULTURE_PARTNERSHIP_FORM_DEFAULTS,
  fieldConfig,
  launchTimelineOptions,
  organizationTypeOptions,
  publiclyReferenceableOptions,
  referralSourceOptions,
} from "./constants";
import type { CulturePartnershipPayload } from "./types";

const formOpts = formOptions({
  defaultValues: CULTURE_PARTNERSHIP_FORM_DEFAULTS,
});

const countWords = (value: string) => value.trim().split(/\s+/).filter(Boolean).length;

const requiredWordCount = (messages: ValidationMessages, maxWords: number, maxWordsMessage: string) => {
  return ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) return messages.required;
    if (countWords(value) > maxWords) return maxWordsMessage;
    return undefined;
  };
};

const annualAudienceReachValidator = (
  messages: ValidationMessages,
  errorMessages: { notANumber: string; minReach: string },
) => {
  return ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) return messages.required;

    const normalizedValue = value.replaceAll(",", "").trim();
    if (!/^\d+$/.test(normalizedValue)) return errorMessages.notANumber;

    const reach = Number.parseInt(normalizedValue, 10);
    if (reach < 30000) return errorMessages.minReach;

    return undefined;
  };
};

const optionalUrlValidator = (messages: ValidationMessages) => {
  const requiredUrlValidator = urlValidator(messages);

  return ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) return undefined;
    return requiredUrlValidator({ value });
  };
};

export interface CulturePartnershipFormProps {
  onSubmit: (payload: CulturePartnershipPayload) => Promise<HubSpotSubmitResult>;
  onSuccess?: () => void;
  onCancel?: () => void;
  useDialogPortal?: boolean;
}

export function CulturePartnershipForm({
  onSubmit,
  onSuccess,
  onCancel,
  useDialogPortal = false,
}: CulturePartnershipFormProps) {
  const t = useTranslations("FormsClientKeys.culture_partnership_form");
  const tValidation = useTranslations("ClientKeys.forms.validation");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { captureEvent } = useAnalytics();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectPortalContainer = useDialogPortal ? containerRef : undefined;

  const messages = useMemo(() => createValidationMessages(tValidation), [tValidation]);
  const validators = useMemo(
    () => ({
      organizationName: requiredString(messages, 200),
      countryRegion: countryValidator(messages),
      organizationWebsite: urlValidator(messages),
      contactName: requiredString(messages, 200),
      contactEmail: emailValidator(messages),
      nonprofitStatusVerification: requiredString(messages, 1000),
      missionStatement: requiredWordCount(messages, 150, t("errors.max_words", { count: 150 })),
      organizationType: requiredString(messages, 200),
      referralSource: requiredString(messages, 200),
      annualAudienceReach: annualAudienceReachValidator(messages, {
        notANumber: t("errors.not_a_number"),
        minReach: t("errors.min_audience_reach"),
      }),
      proposedCollaborationType: requiredString(messages, 1000),
      partnershipDescription: requiredWordCount(messages, 300, t("errors.max_words", { count: 300 })),
      launchTimeline: requiredString(messages, 200),
      publiclyReferenceable: requiredString(messages, 20),
      pressKitUrl: optionalUrlValidator(messages),
      additionalNotes: optionalString(messages, 5000),
    }),
    [messages, t],
  );

  const organizationTypeFieldOptions = useMemo(
    () => organizationTypeOptions.map(opt => ({ label: t(opt.translationKey), value: opt.value })),
    [t],
  );
  const referralSourceFieldOptions = useMemo(
    () => referralSourceOptions.map(opt => ({ label: t(opt.translationKey), value: opt.value })),
    [t],
  );
  const launchTimelineFieldOptions = useMemo(
    () => launchTimelineOptions.map(opt => ({ label: t(opt.translationKey), value: opt.value })),
    [t],
  );
  const publiclyReferenceableFieldOptions = useMemo(
    () => publiclyReferenceableOptions.map(opt => ({ label: t(opt.translationKey), value: opt.value })),
    [t],
  );

  const form = useAppForm({
    ...formOpts,
    onSubmit: async ({ value }) => {
      setSubmitError(null);

      const payload: CulturePartnershipPayload = enrichWithUTMs(value);

      try {
        const result = await onSubmit(payload);
        if (result.success) {
          captureEvent(EventName.mw_culture_partnership_form_submitted, {});
          onSuccess?.();
        } else {
          setSubmitError(result.error || t("errors.submission_failed"));
        }
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "[CulturePartnershipForm] Failed to submit form");
        setSubmitError(t("errors.submission_failed"));
        throw error;
      }
    },
  });

  return (
    <div ref={containerRef} className='tw-w-full'>
      <form
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField
          name={fieldConfig.organizationName.name}
          validators={{ onMount: validators.organizationName, onChange: validators.organizationName }}
        >
          {field => (
            <field.Input
              name={fieldConfig.organizationName.name}
              label={t("fields.organization_name.label")}
              placeholder={t("fields.organization_name.placeholder")}
              required={fieldConfig.organizationName.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.countryRegion.name}
          validators={{ onMount: validators.countryRegion, onChange: validators.countryRegion }}
        >
          {field => (
            <field.CountrySelect
              name={fieldConfig.countryRegion.name}
              label={t("fields.country_region.label")}
              required={fieldConfig.countryRegion.required}
              showTooltip={false}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.organizationWebsite.name}
          validators={{ onMount: validators.organizationWebsite, onChange: validators.organizationWebsite }}
        >
          {field => (
            <field.Input
              name={fieldConfig.organizationWebsite.name}
              label={t("fields.organization_website.label")}
              placeholder={t("fields.organization_website.placeholder")}
              required={fieldConfig.organizationWebsite.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.contactName.name}
          validators={{ onMount: validators.contactName, onChange: validators.contactName }}
        >
          {field => (
            <field.Input
              name={fieldConfig.contactName.name}
              label={t("fields.contact_name.label")}
              placeholder={t("fields.contact_name.placeholder")}
              required={fieldConfig.contactName.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.contactEmail.name}
          validators={{ onMount: validators.contactEmail, onChange: validators.contactEmail }}
        >
          {field => (
            <field.Input
              name={fieldConfig.contactEmail.name}
              label={t("fields.contact_email.label")}
              placeholder={t("fields.contact_email.placeholder")}
              type='email'
              required={fieldConfig.contactEmail.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.nonprofitStatusVerification.name}
          validators={{
            onMount: validators.nonprofitStatusVerification,
            onChange: validators.nonprofitStatusVerification,
          }}
        >
          {field => (
            <field.Input
              name={fieldConfig.nonprofitStatusVerification.name}
              label={t("fields.nonprofit_status_verification.label")}
              placeholder={t("fields.nonprofit_status_verification.placeholder")}
              required={fieldConfig.nonprofitStatusVerification.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.missionStatement.name}
          validators={{ onMount: validators.missionStatement, onChange: validators.missionStatement }}
        >
          {field => (
            <field.Textarea
              name={fieldConfig.missionStatement.name}
              label={t("fields.mission_statement.label")}
              placeholder={t("fields.mission_statement.placeholder")}
              required={fieldConfig.missionStatement.required}
              rows={4}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.organizationType.name}
          validators={{ onMount: validators.organizationType, onChange: validators.organizationType }}
        >
          {field => (
            <field.Select
              name={fieldConfig.organizationType.name}
              label={t("fields.organization_type.label")}
              unselectedLabel={t("fields.organization_type.unselectedLabel")}
              options={organizationTypeFieldOptions}
              required={fieldConfig.organizationType.required}
              portalContainer={selectPortalContainer}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.referralSource.name}
          validators={{ onMount: validators.referralSource, onChange: validators.referralSource }}
        >
          {field => (
            <field.Select
              name={fieldConfig.referralSource.name}
              label={t("fields.referral_source.label")}
              unselectedLabel={t("fields.referral_source.unselectedLabel")}
              options={referralSourceFieldOptions}
              required={fieldConfig.referralSource.required}
              portalContainer={selectPortalContainer}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.annualAudienceReach.name}
          validators={{ onMount: validators.annualAudienceReach, onChange: validators.annualAudienceReach }}
        >
          {field => (
            <field.Input
              name={fieldConfig.annualAudienceReach.name}
              label={t("fields.annual_audience_reach.label")}
              placeholder={t("fields.annual_audience_reach.placeholder")}
              required={fieldConfig.annualAudienceReach.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.proposedCollaborationType.name}
          validators={{
            onMount: validators.proposedCollaborationType,
            onChange: validators.proposedCollaborationType,
          }}
        >
          {field => (
            <field.Input
              name={fieldConfig.proposedCollaborationType.name}
              label={t("fields.proposed_collaboration_type.label")}
              placeholder={t("fields.proposed_collaboration_type.placeholder")}
              required={fieldConfig.proposedCollaborationType.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.partnershipDescription.name}
          validators={{ onMount: validators.partnershipDescription, onChange: validators.partnershipDescription }}
        >
          {field => (
            <field.Textarea
              name={fieldConfig.partnershipDescription.name}
              label={t("fields.partnership_description.label")}
              placeholder={t("fields.partnership_description.placeholder")}
              required={fieldConfig.partnershipDescription.required}
              rows={5}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.launchTimeline.name}
          validators={{ onMount: validators.launchTimeline, onChange: validators.launchTimeline }}
        >
          {field => (
            <field.Select
              name={fieldConfig.launchTimeline.name}
              label={t("fields.launch_timeline.label")}
              unselectedLabel={t("fields.launch_timeline.unselectedLabel")}
              options={launchTimelineFieldOptions}
              required={fieldConfig.launchTimeline.required}
              portalContainer={selectPortalContainer}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.publiclyReferenceable.name}
          validators={{ onMount: validators.publiclyReferenceable, onChange: validators.publiclyReferenceable }}
        >
          {field => (
            <field.Select
              name={fieldConfig.publiclyReferenceable.name}
              label={t("fields.publicly_referenceable.label")}
              unselectedLabel={t("fields.publicly_referenceable.unselectedLabel")}
              options={publiclyReferenceableFieldOptions}
              required={fieldConfig.publiclyReferenceable.required}
              portalContainer={selectPortalContainer}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.pressKitUrl.name}
          validators={{ onMount: validators.pressKitUrl, onChange: validators.pressKitUrl }}
        >
          {field => (
            <field.Input
              name={fieldConfig.pressKitUrl.name}
              label={t("fields.press_kit_url.label")}
              placeholder={t("fields.press_kit_url.placeholder")}
              required={fieldConfig.pressKitUrl.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.additionalNotes.name}
          validators={{ onMount: validators.additionalNotes, onChange: validators.additionalNotes }}
        >
          {field => (
            <field.Textarea
              name={fieldConfig.additionalNotes.name}
              label={t("fields.additional_notes.label")}
              placeholder={t("fields.additional_notes.placeholder")}
              required={fieldConfig.additionalNotes.required}
              rows={4}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={state => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting })}>
          {({ canSubmit, isSubmitting }) => (
            <div className='tw-flex tw-items-end tw-gap-4'>
              {onCancel && (
                <Button type='button' variant='secondary' size='lg' className='tw-flex-1' onClick={onCancel}>
                  {t("buttons.cancel")}
                </Button>
              )}
              <div className={onCancel ? "tw-flex-1" : "tw-w-full"}>
                <SubmitButton
                  canSubmit={canSubmit}
                  isSubmitting={isSubmitting}
                  submitLabel={t("buttons.submit")}
                  submittingLabel={t("buttons.submitting")}
                  submitError={submitError}
                />
              </div>
            </div>
          )}
        </form.Subscribe>
      </form>
    </div>
  );
}
